<template>
  <div class="video-page-container" data-external="video-page-container-element">
    <VideoPlayer
      v-if="!page.loading"
      :url="page.video && page.video.videoUrl"
      :thumb-url="thumbUrl"
      :video-id="page.video && page.video.id"
      ratio="16:9"
      @timeupdate="handleTimeUpdate"
      @pause="handlePause"
      @ended="handleEnded"
    />
  </div>
</template>
<script lang="ts">
import { useI18n } from 'vue-i18n';
import type { PropType } from 'vue';
import { defineComponent, ref, computed } from 'vue';
import { OptionMenuItemCallback } from '../options-menu/types/enums/option-menu-item-callback';
import type { DocumentPage } from '../types/document-page';
import VideoPlayer from '../video/player/components/VideoPlayer.vue';

export default defineComponent({
  components: {
    VideoPlayer,
  },
  props: {
    page: {
      type: Object as PropType<DocumentPage>,
      required: true,
    },
  },
  emits: ['track-video'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const currentVideoPlayTime = ref<number>(null);
    const showOptions = ref(false);

    const thumbUrl = computed(() => props.page.video?.thumbUrl);

    const options = computed(() => [
      {
        icon: 'edit',
        title: t('edit'),
        callback: OptionMenuItemCallback.Edit,
      },
      {
        icon: 'chevron-up-and-down',
        title: t('move'),
        callback: OptionMenuItemCallback.Move,
      },
      {
        icon: 'delete-bin',
        title: t('delete'),
        callback: OptionMenuItemCallback.Delete,
      },
    ]);

    const handleTimeUpdate = (playtime: number) => {
      currentVideoPlayTime.value = playtime;
    };

    const handlePause = () => {
      sendTrackingData();
    };

    const handleEnded = () => {
      sendTrackingData();
    };

    const handleShowOptions = () => {
      showOptions.value = true;
    };

    const hideOptions = () => {
      showOptions.value = false;
    };

    const sendTrackingData = () => {
      if (!currentVideoPlayTime.value) {
        return;
      }
      emit('track-video', { id: props.page.video.id, viewDuration: currentVideoPlayTime.value });
      currentVideoPlayTime.value = null;
    };

    return {
      currentVideoPlayTime,
      thumbUrl,
      options,
      showOptions,
      handleTimeUpdate,
      handlePause,
      handleEnded,
      handleShowOptions,
      hideOptions,
      sendTrackingData,
    };
  },
});
</script>
<style lang="scss" scoped>
.video-page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-medium);
  overflow: hidden;

  @media only screen and (max-width: $xs) {
    border-radius: 0;
  }
}
</style>
